import { useRequest } from 'ahooks';
import { getPlatformData } from './service';
import { Drawer, DrawerProps, message, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { initEcharts } from 'lib/echarts';
import { formatNumber } from 'utils/numberFormat';
import { getChartMaxValue } from '../Activity/useActivityContents';
import { isStoreAliasFirst, useStoreNameConfig } from 'hooks/business/store/useStoreName';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslate } from '@tolgee/react';
import { QuickRangePicker } from 'components/QuickRangePicker';
import { What } from 'views/Aide/Scenario/PolicyTable';
import { Container } from './ShopeeTrendDrawer';

function LazadaTrendDrawer({
  storePerfData,
  ...otherProps
}: DrawerProps & {
  storePerfData: Dashboard.PlatformData.StorePerfData;
}) {
  const { region, storeId, storeName, storeAlias } = storePerfData;

  const { t } = useTranslate();

  const { config } = useStoreNameConfig();

  const name = isStoreAliasFirst(config, 'LAZADA', region) ? storeAlias || storeName : storeName;

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, 'day').startOf('day'),
    dayjs().subtract(1, 'day').endOf('day'),
  ]);

  const { loading, data } = useRequest(
    () =>
      getPlatformData({
        storeId: storeId,
        startTime: dateRange[0].format('YYYY-MM-DD HH:mm:ss'),
        endTime: dateRange[1].format('YYYY-MM-DD HH:mm:ss'),
      }).then((res) => {
        return res
          .map(({ data, ...others }) => {
            return {
              ...others,
              data: (JSON.parse(data) as any[]).reduce<any>((res, cur) => {
                res[cur.type] = cur.score;
                return res;
              }, {}),
            };
          })
          .reverse();
      }),
    {
      refreshDeps: [storeId, dateRange],
      onError: () => message.error(t('phrase.获取数据失败，请稍后再试')),
    },
  );

  const chartRef1 = useRef<HTMLDivElement>(null);
  const chartRef2 = useRef<HTMLDivElement>(null);
  const chartRef3 = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!data || !chartRef1.current || !chartRef2.current || !chartRef3.current) {
      return;
    }

    const chart1 = initEcharts(chartRef1.current);
    const chart2 = initEcharts(chartRef2.current);
    const chart3 = initEcharts(chartRef3.current);

    const percentFormatter = (value: any) =>
      typeof value === 'number' ? formatNumber(value / 100, '0.0%') : '-';

    const dateList = data.map(({ countDt }) => countDt);

    const tooltipFormatter = (
      params: any,
      start: number,
      end: number,
      target?: string,
      formatter = percentFormatter,
    ) => {
      const date = dayjs(params[0].name);
      const line1 = `<div style="margin-bottom: 2px; color: #515359;">${date.format('MM/DD')} (${
        [
          t('multiTimeSelect.周日'),
          t('multiTimeSelect.周一'),
          t('multiTimeSelect.周二'),
          t('multiTimeSelect.周三'),
          t('multiTimeSelect.周四'),
          t('multiTimeSelect.周五'),
          t('multiTimeSelect.周六'),
        ][date.day()]
      })</div>`;
      const line2 = `<div style="margin-bottom: 4px; font-size: 12px; color: #84868C;">${t(
        '统计区间 : x-y',
        {
          x: date.subtract(start, 'days').format('MM/DD'),
          y: date.subtract(end, 'days').format('MM/DD'),
        },
      )}</div>`;
      const line3 = `<div style="margin-bottom: 1px; display: flex; justify-content: space-between; alignitems: center; font-size: 12px; color: #84868C;"><span>${
        params[0].marker
      } ${params[0].seriesName}</span> <span style="font-weight: bold; color: #232426;">${formatter(
        params[0].value,
      )}</span></div>`;
      const line4 = target
        ? `<div style="margin-bottom: 1px; display: flex; justify-content: space-between; alignitems: center; font-size: 12px; color: #84868C;"><span>${params[1].marker} ${params[1].seriesName}</span> <span style="font-weight: bold; color: #232426;">${target}</span></div>`
        : '';
      return `<div>${line1}${line2}${line3}${line4}</div>`;
    };

    chart1.setOption({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => tooltipFormatter(params, 57, 2),
      },
      legend: {
        selectedMode: false,
        data: [t('买家好评率')],
        top: 0,
        left: 'center',
      },
      grid: {
        left: '2%',
        right: '2%',
        top: '10%',
        bottom: '2%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: dateList,
        axisLabel: {
          formatter: (value: string) => value.slice(5),
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        interval: 20,
        axisLabel: {
          formatter: (num: number) => `${num}%`,
        },
      },
      series: [
        {
          name: t('买家好评率'),
          type: 'line',
          data: data.map(({ data }) => data.POSITIVE_SELLER_RATING),
          smooth: true,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: 'rgb(91, 143, 249)',
          },
          areaStyle: {
            color: 'rgba(91, 143, 249, .3)',
          },
        },
      ],
    });

    chart2.setOption({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => tooltipFormatter(params, 7, 1, '≥90%'),
      },
      legend: {
        selectedMode: false,
        data: [t('回复率'), t('目标')],
        top: 0,
        left: 'center',
      },
      grid: {
        left: '2%',
        right: '3%',
        top: '10%',
        bottom: '2%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: dateList,
        axisLabel: {
          formatter: (value: string) => value.slice(5),
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        interval: 20,
        axisLabel: {
          formatter: (num: number) => `${num}%`,
        },
      },
      series: [
        {
          name: t('回复率'),
          type: 'line',
          data: data.map(({ data }) => data.CHAT_RESPONSE_RATE),
          smooth: true,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: 'rgb(97, 221, 170)',
          },
          areaStyle: {
            color: 'rgba(97, 221, 170, .3)',
          },
        },
        {
          name: t('目标'),
          type: 'line',
          data: data.map((_) => 90),
          smooth: true,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: 'rgb(246, 189, 22)',
          },
          areaStyle: {
            color: 'rgba(246, 189, 22, .3)',
          },
        },
      ],
    });

    const chatResponseTimeList = data.map(({ data }) => data.CHAT_RESPONSE_TIME);
    const max = getChartMaxValue(
      [60, ...chatResponseTimeList.filter((_) => typeof _ === 'number')],
      5,
    );

    chart3.setOption({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) =>
          tooltipFormatter(params, 7, 1, `≤60 ${t('marketing.分钟')}`, (value: any) =>
            typeof value === 'number' ? `${value} ${t('marketing.分钟')}` : '-',
          ),
      },
      legend: {
        selectedMode: false,
        data: [t('平均响应时长'), t('目标')],
        top: 0,
        left: 'center',
      },
      grid: {
        left: '2%',
        right: '3%',
        top: '10%',
        bottom: '2%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: dateList,
        axisLabel: {
          formatter: (value: string) => value.slice(5),
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: max,
        splitNumber: 5,
        interval: max / 5,
        name: t('marketing.分钟'),
      },
      series: [
        {
          name: t('平均响应时长'),
          type: 'line',
          data: chatResponseTimeList,
          smooth: true,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: 'rgb(114, 98, 253)',
          },
          areaStyle: {
            color: 'rgba(114, 98, 253, .3)',
          },
        },
        {
          name: t('目标'),
          type: 'line',
          data: data.map((_) => 60),
          smooth: true,
          showSymbol: false,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: 'rgb(246, 189, 22)',
          },
          areaStyle: {
            color: 'rgba(246, 189, 22, .3)',
          },
        },
      ],
    });

    const handleResize = () => {
      chart1.resize();
      chart2.resize();
      chart3.resize();
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart1.dispose();
      chart2.dispose();
      chart3.dispose();
    };
  }, [data, t]);

  if (!data || !storeId) {
    return <>{loading ? <LoadingOutlined /> : <></>}</>;
  }

  return (
    <Drawer title={`${t('查看趋势')} - ${name}`} width="85vw" {...otherProps}>
      <Container>
        <header>
          <QuickRangePicker
            quickActions={[
              {
                key: 'last7days',
                label: t('remind.近x天', { x: 7 }),
                range: [
                  dayjs().subtract(7, 'day').startOf('day'),
                  dayjs().subtract(1, 'day').endOf('day'),
                ],
              },
              {
                key: 'last30days',
                label: t('remind.近x天', { x: 30 }),
                range: [
                  dayjs().subtract(30, 'day').startOf('day'),
                  dayjs().subtract(1, 'day').endOf('day'),
                ],
              },
            ]}
            value={dateRange}
            onChange={(range) => {
              const [start, end] = range || [];
              if (start && end) {
                setDateRange([start, end]);
              }
            }}
          />
        </header>
        <div>
          <section>
            <header>
              <h3>
                {t('买家好评率')}
                <Tooltip title={t('买家给予店铺的好评数/总评价数')}>
                  <What />
                </Tooltip>
              </h3>
            </header>
            <div ref={chartRef1} />
          </section>
          <div>
            <section>
              <header>
                <h3>
                  {t('回复率')}
                  <Tooltip
                    title={t(
                      '过去30天（节假日除外）回复率的平均值，当地时间n-1天18:00到n天18:00的咨询算作第n天的咨询，其中客服在n天19:00前有回复的消息算在回复率内。',
                    )}
                  >
                    <What />
                  </Tooltip>
                </h3>
              </header>
              <div ref={chartRef2} />
            </section>
            <section>
              <header>
                <h3>
                  {t('平均响应时长')}
                  <Tooltip title={t('卖家侧首次回复客户的平均时长')}>
                    <What />
                  </Tooltip>
                </h3>
              </header>
              <div ref={chartRef3} />
            </section>
          </div>
        </div>
      </Container>
    </Drawer>
  );
}

export default LazadaTrendDrawer;
